import { RUNTIME_API_BASE_URL } from '../config/runtimeApi';
import {
  ApiError,
  appendUploadFileToFormData,
  createApiClient,
  getStoredAccessToken,
} from './client';
import type {
  PhotoSearchAcceptedDto,
  PhotoSearchAcceptedViewModel,
  PhotoSearchAnswerPacketDto,
  PhotoSearchAnswerPacketViewModel,
  PhotoSearchConversationViewModel,
  PhotoSearchFinalEventDto,
  PhotoSearchImageAssetViewModel,
  PhotoSearchKnowledgeImpactViewModel,
  PhotoSearchMatchDto,
  PhotoSearchMatchResponseDto,
  PhotoSearchMatchViewModel,
  PhotoSearchMessageViewModel,
  PhotoSearchSafetyViewModel,
  PhotoSearchSectionDto,
  PhotoSearchSectionItemDto,
  PhotoSearchSectionItemViewModel,
  PhotoSearchSectionViewModel,
  PhotoSearchSessionDto,
  PhotoSearchStreamEventDto,
  PhotoSearchUploadDto,
  PhotoSearchUploadPurpose,
  PhotoSearchUploadViewModel,
} from '../types/photoSearch';
import { createPhotoSearchSseDecoder, deliverPhotoSearchSseText } from '../utils/photoSearchSse';
import { resolveRuntimeApiUrl } from '../utils/runtimeApiSupport';

export const PHOTO_SEARCH_ENDPOINTS = {
  uploadImage: '/api/v1/uploads/image',
  createSession: '/api/v2/agent/sessions',
  solveProblem: '/api/v2/agent/problems/solve',
  matchProblems: '/api/v2/agent/problems/match',
  stream: '/api/v2/agent/streams',
  reply: '/api/v2/agent/sessions',
} as const;

const DEFAULT_UPLOAD_OPTIONS: { purpose: PhotoSearchUploadPurpose } = { purpose: 'search' };
const AGENT_SETUP_REQUEST_OPTIONS = { timeoutMs: 60_000 } as const;
const PHOTO_MATCH_REQUEST_OPTIONS = { timeoutMs: 60_000 } as const;

const STAGE_LABELS: Record<string, string> = {
  ack: '正在连接 AI 讲解',
  image_uploading: '正在上传题图',
  vision: '正在识别题目',
  parser: '正在解析题意',
  solver: '正在生成解法',
  student_model: '正在理解追问',
  tutor: '正在输出讲解',
  verifier: '正在核对答案',
  done: '输出完成',
};

const POLICY_OVERRIDE = {
  stuck_threshold: 1,
  force_socratic_style: false,
  allow_final_answer_line: true,
} as const;

type PhotoSearchAgentMode = 'explain' | 'correct' | 'paper_analysis' | 'search';

type PhotoSearchAgentContextRef = {
  type: string;
  id: string;
  title?: string | null;
  metadata?: Record<string, unknown>;
};

type PhotoSearchSolveRequest = {
  session_id: string;
  message_text: string;
  image_refs: string[];
  segment_id?: string;
  mode: PhotoSearchAgentMode;
  context_refs: PhotoSearchAgentContextRef[];
  policy_override: typeof POLICY_OVERRIDE;
  stream: true;
};

type PhotoSearchReplyRequest = {
  message_text: string;
  mode: PhotoSearchAgentMode;
  context_refs: PhotoSearchAgentContextRef[];
  stream: true;
};

type NormalizedStreamPayload = {
  type: string;
  data: Record<string, unknown>;
};

const trimTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

const getPhotoSearchApiBaseUrl = (): string => {
  return trimTrailingSlash(RUNTIME_API_BASE_URL);
};

const createPhotoSearchClient = () => createApiClient({
  baseUrl: getPhotoSearchApiBaseUrl(),
  getToken: getStoredAccessToken,
});

const buildBackendUrl = (path: string): string => {
  return resolveRuntimeApiUrl(getPhotoSearchApiBaseUrl(), path);
};

const buildAgentReplyPath = (sessionId: string): string =>
  `${PHOTO_SEARCH_ENDPOINTS.reply}/${encodeURIComponent(sessionId)}/reply`;

const createMessageId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const asBoolean = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const asSectionDtos = (value: unknown): PhotoSearchSectionDto[] =>
  Array.isArray(value) ? (value as PhotoSearchSectionDto[]) : [];

const asAnswerPacketDto = (value: unknown): PhotoSearchAnswerPacketDto | null =>
  asRecord(value) ? (value as PhotoSearchAnswerPacketDto) : null;

export const getPhotoSearchStageLabel = (stage: string | null, fallback = ''): string => {
  if (stage && STAGE_LABELS[stage]) {
    return STAGE_LABELS[stage];
  }
  return fallback || stage || STAGE_LABELS.ack;
};

export const mapPhotoSearchUploadDtoToViewModel = (
  dto: PhotoSearchUploadDto
): PhotoSearchUploadViewModel => {
  const imageRef = dto.image_ref?.trim();
  if (!imageRef) {
    throw new ApiError('图片上传成功但缺少 image_ref', 'parse');
  }

  return {
    imageRef,
    uploadId: dto.upload_id ?? null,
    url: dto.url ? buildBackendUrl(dto.url) : null,
    fileName: dto.filename ?? null,
    contentType: dto.content_type ?? null,
    sizeBytes: dto.size_bytes ?? 0,
    purpose: dto.purpose ?? null,
    studentId: dto.student_id ?? null,
    createdAt: dto.created_at ?? null,
  };
};

export const mapPhotoSearchAcceptedDtoToViewModel = (
  dto: PhotoSearchAcceptedDto
): PhotoSearchAcceptedViewModel => {
  const requestId = dto.request_id?.trim();
  if (!requestId) {
    throw new ApiError('后端未返回讲解请求 ID', 'parse');
  }

  return {
    requestId,
    sessionId: dto.session_id ?? null,
    streamUrl: dto.stream_url ?? null,
    conversationId: dto.conversation_id ?? null,
  };
};

export const mapPhotoSearchMatchDtoToViewModel = (
  dto: PhotoSearchMatchDto,
  index = 0
): PhotoSearchMatchViewModel => {
  const problemId = dto.problem_id?.trim();
  const segmentId = dto.segment_id?.trim() ?? null;
  const fallbackId = segmentId ?? `match-${index + 1}`;
  const rawConfidence = dto.confidence ?? 0;
  const confidencePercent =
    rawConfidence <= 1 ? Math.round(rawConfidence * 100) : Math.round(rawConfidence);
  const ocrText = dto.ocr_text?.trim() ?? '';
  const normalizedProblem = dto.normalized_problem?.trim() ?? '';
  const description =
    dto.stem?.trim() ||
    normalizedProblem ||
    ocrText ||
    dto.content?.trim() ||
    dto.answer?.trim() ||
    '暂无题干预览';

  return {
    id: problemId ?? fallbackId,
    segmentId,
    title: dto.title?.trim() || `匹配题目 ${index + 1}`,
    description,
    ocrText,
    normalizedProblem,
    subject: dto.subject ?? null,
    grade: dto.grade ?? null,
    confidencePercent: Math.min(100, Math.max(0, confidencePercent)),
    sourceLabel: dto.source?.trim() || '题库匹配',
    knowledgePoints: dto.knowledge_points ?? [],
    needsReview: dto.needs_review ?? false,
    cropImageRef: dto.crop_image_ref ?? null,
    bounds: dto.bbox ?? null,
    detectionOrder: index + 1,
    questionNumber: dto.question_number ?? null,
    subquestionLabel: dto.subquestion_label ?? null,
  };
};

const mapSectionItem = (
  item: PhotoSearchSectionItemDto,
  index: number,
  prefix: string
): PhotoSearchSectionItemViewModel => {
  if (typeof item === 'string') {
    return {
      id: `${prefix}-item-${index}`,
      label: null,
      value: item,
      step: String(index + 1),
    };
  }

  const value = item.content ?? item.value ?? item.text ?? '';
  const label = item.label ?? null;

  return {
    id: `${prefix}-item-${index}`,
    label,
    value: String(value),
    step: item.step === undefined || item.step === null ? null : String(item.step),
  };
};

export const mapPhotoSearchSectionDtoToViewModel = (
  dto: PhotoSearchSectionDto,
  index = 0
): PhotoSearchSectionViewModel => {
  const id = dto.id?.trim() || `section-${index + 1}`;
  const title = dto.title?.trim() || `讲解 ${index + 1}`;
  return {
    id,
    title,
    items: (dto.items ?? []).map((item, itemIndex) => mapSectionItem(item, itemIndex, id)),
  };
};

export const mapPhotoSearchAnswerPacketDtoToViewModel = (
  dto: PhotoSearchAnswerPacketDto | null
): PhotoSearchAnswerPacketViewModel | null => {
  if (!dto) {
    return null;
  }

  const steps = dto.solution?.steps ?? [];

  return {
    brief: dto.brief ?? '',
    steps: steps.map((item, index) => mapSectionItem(item, index, 'answer-step')),
    finalAnswer: dto.solution?.final_answer ?? '',
  };
};

const mapPhotoSearchSafety = (
  meta: Record<string, unknown> | null,
  needMoreInput: boolean
): PhotoSearchSafetyViewModel => {
  const safetyStatus = asString(meta?.safety_status);
  const confidenceLevel = asString(meta?.confidence_level);

  if (safetyStatus === 'blocked') {
    return {
      status: 'blocked',
      message:
        asString(meta?.safety_message) ??
        '这次内容暂时无法直接生成讲解，请换一张清晰题图或重新提问。',
    };
  }

  if (confidenceLevel === 'low' || needMoreInput) {
    return {
      status: 'low_confidence',
      message:
        asString(meta?.confidence_message) ??
        'AI 对题目识别不够确定，请先核对题干或继续补充信息。',
    };
  }

  return {
    status: 'normal',
    message: '',
  };
};

const mapPhotoSearchKnowledgeImpacts = (
  meta: Record<string, unknown> | null
): PhotoSearchKnowledgeImpactViewModel[] => {
  const rawImpacts = Array.isArray(meta?.knowledge_impacts) ? meta.knowledge_impacts : [];
  const topicTags = rawImpacts.length ? [] : asStringArray(meta?.topic_tags);
  const impacts = rawImpacts.length
    ? rawImpacts
    : topicTags.map((tag) => ({
        knowledge_point: tag,
        subject: '综合',
        change_label: '本次讲解涉及',
      }));

  return impacts
    .map((value, index) => {
      const record = asRecord(value);
      if (!record) {
        return null;
      }
      const knowledgePoint = asString(record.knowledge_point) ?? asString(record.label);
      if (!knowledgePoint) {
        return null;
      }
      return {
        id: asString(record.id) ?? `knowledge-impact-${index + 1}`,
        subject: asString(record.subject) ?? '综合',
        knowledgePoint,
        beforeStatus: asString(record.before_status) ?? '未评估',
        afterStatus: asString(record.after_status) ?? '待巩固',
        changeLabel: asString(record.change_label) ?? '已根据本次讲解更新',
      };
    })
    .filter((impact): impact is PhotoSearchKnowledgeImpactViewModel => Boolean(impact));
};

export const buildPhotoSearchStreamUrl = (requestIdOrPath: string | null): string => {
  if (!requestIdOrPath) {
    return buildBackendUrl(PHOTO_SEARCH_ENDPOINTS.stream);
  }

  if (/^https?:\/\//i.test(requestIdOrPath) || requestIdOrPath.startsWith('/')) {
    return buildBackendUrl(requestIdOrPath);
  }

  return buildBackendUrl(
    `${PHOTO_SEARCH_ENDPOINTS.stream}/${encodeURIComponent(requestIdOrPath)}`
  );
};

export const consumePhotoSearchStream = async (
  requestIdOrPath: string,
  onEvent: (event: PhotoSearchStreamEventDto) => void,
  signal?: AbortSignal
): Promise<void> => {
  const token = await getStoredAccessToken();
  let response: Response;
  try {
    response = await fetch(buildPhotoSearchStreamUrl(requestIdOrPath), {
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    });
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    throw new ApiError('AI 讲解流连接失败', 'network');
  }

  if (!response.ok) {
    const code = response.status === 401 ? 'unauthorized' : response.status >= 500 ? 'server' : 'unknown';
    throw new ApiError('AI 讲解流请求失败', code, response.status);
  }

  try {
    if (response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader();
      const textDecoder = new TextDecoder();
      const sseDecoder = createPhotoSearchSseDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseDecoder.push(textDecoder.decode(value, { stream: true })).forEach(onEvent);
      }
      const trailingText = textDecoder.decode();
      if (trailingText) {
        sseDecoder.push(trailingText).forEach(onEvent);
      }
      sseDecoder.finish().forEach(onEvent);
    } else {
      deliverPhotoSearchSseText(await response.text(), onEvent);
    }
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'AI 讲解流解析失败',
      'parse'
    );
  }
};

export const uploadPhotoSearchImage = async (
  imageAsset: PhotoSearchImageAssetViewModel,
  purpose: PhotoSearchUploadPurpose = DEFAULT_UPLOAD_OPTIONS.purpose
): Promise<PhotoSearchUploadViewModel> => {
  const formData = new FormData();
  formData.append('purpose', purpose);
  await appendUploadFileToFormData(formData, 'file', imageAsset);
  const response = await createPhotoSearchClient().postForm<PhotoSearchUploadDto>(
    PHOTO_SEARCH_ENDPOINTS.uploadImage,
    formData
  );
  return mapPhotoSearchUploadDtoToViewModel(response.data);
};

export const createPhotoSearchSession = async (): Promise<string> => {
  const response = await createPhotoSearchClient().post<
    PhotoSearchSessionDto,
    { student_id: null; policy_override: null }
  >(PHOTO_SEARCH_ENDPOINTS.createSession, {
    student_id: null,
    policy_override: null,
  }, AGENT_SETUP_REQUEST_OPTIONS);

  const sessionId = response.data.session_id?.trim();
  if (!sessionId) {
    throw new ApiError('后端未返回会话 ID', 'parse');
  }
  return sessionId;
};

export const matchPhotoSearchProblems = async (
  imageRef: string
): Promise<PhotoSearchMatchViewModel[]> => {
  const response = await createPhotoSearchClient().post<
    PhotoSearchMatchResponseDto,
    { image_ref: string }
  >(
    PHOTO_SEARCH_ENDPOINTS.matchProblems,
    { image_ref: imageRef },
    PHOTO_MATCH_REQUEST_OPTIONS
  );

  return (response.data.matches ?? []).map(mapPhotoSearchMatchDtoToViewModel);
};

export const solvePhotoSearchProblem = async (
  sessionId: string,
  imageRef: string,
  selectedMatch: PhotoSearchMatchViewModel | null = null,
  contextRefs: PhotoSearchAgentContextRef[] = []
): Promise<PhotoSearchAcceptedViewModel> => {
  const body: PhotoSearchSolveRequest = {
    session_id: sessionId,
    message_text: '',
    image_refs: imageRef ? [imageRef] : [],
    mode: 'explain',
    context_refs: contextRefs,
    policy_override: POLICY_OVERRIDE,
    stream: true,
    ...(selectedMatch?.segmentId ? { segment_id: selectedMatch.segmentId } : {}),
  };

  const response = await createPhotoSearchClient().post<PhotoSearchAcceptedDto, PhotoSearchSolveRequest>(
    PHOTO_SEARCH_ENDPOINTS.solveProblem,
    body,
    AGENT_SETUP_REQUEST_OPTIONS
  );
  return mapPhotoSearchAcceptedDtoToViewModel(response.data);
};

export const replyToPhotoSearchSession = async (
  sessionId: string,
  messageText: string,
  contextRefs: PhotoSearchAgentContextRef[] = []
): Promise<PhotoSearchAcceptedViewModel> => {
  const response = await createPhotoSearchClient().post<PhotoSearchAcceptedDto, PhotoSearchReplyRequest>(
    buildAgentReplyPath(sessionId),
    {
      message_text: messageText.trim(),
      mode: 'explain',
      context_refs: contextRefs,
      stream: true,
    }
  );
  return mapPhotoSearchAcceptedDtoToViewModel(response.data);
};

const createAssistantMessage = (requestId: string): PhotoSearchMessageViewModel => ({
  id: createMessageId(`assistant-${requestId || 'pending'}`),
  role: 'assistant',
  kind: 'assistant_streaming',
  content: '',
  stage: STAGE_LABELS.ack,
  sections: [],
  answerPacket: null,
  problemId: null,
  needMoreInput: false,
  safety: { status: 'normal', message: '' },
  knowledgeImpacts: [],
});

export const createInitialPhotoSearchConversation = (
  image: PhotoSearchImageAssetViewModel,
  imageRef: string,
  sessionId: string,
  accepted: PhotoSearchAcceptedViewModel,
  selectedMatch: PhotoSearchMatchViewModel | null = null
): PhotoSearchConversationViewModel => ({
  image,
  imageRef,
  selectedMatch,
  sessionId,
  requestId: accepted.requestId,
  streamUrl: accepted.streamUrl,
  conversationId: accepted.conversationId,
  problemId: selectedMatch?.id ?? null,
  streamStatus: 'connecting',
  currentStage: STAGE_LABELS.ack,
  currentStageKey: 'ack',
  canContinue: false,
  needMoreInput: false,
  loading: true,
  errorMessage: '',
  knowledgeImpacts: [],
  messages: [
    {
      id: createMessageId('user-image'),
      role: 'user',
      kind: 'user_image',
      content: '我上传了这道题，请帮我讲解。',
      imageUri: image.uri,
      imageRef,
    },
    createAssistantMessage(accepted.requestId),
  ],
});

export const appendPhotoSearchFollowUpTurn = (
  conversation: PhotoSearchConversationViewModel,
  messageText: string,
  accepted: PhotoSearchAcceptedViewModel
): PhotoSearchConversationViewModel => ({
  ...conversation,
  requestId: accepted.requestId,
  streamUrl: accepted.streamUrl,
  conversationId: accepted.conversationId ?? conversation.conversationId,
  streamStatus: 'connecting',
  currentStage: STAGE_LABELS.ack,
  currentStageKey: 'ack',
  canContinue: false,
  loading: true,
  errorMessage: '',
  messages: [
    ...conversation.messages,
    {
      id: createMessageId('user-text'),
      role: 'user',
      kind: 'user_text',
      content: messageText.trim(),
    },
    createAssistantMessage(accepted.requestId),
  ],
});

const getLastAssistantIndex = (messages: PhotoSearchMessageViewModel[]): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'assistant') {
      return index;
    }
  }
  return -1;
};

const ensureStreamingAssistant = (
  messages: PhotoSearchMessageViewModel[],
  requestId: string
): { messages: PhotoSearchMessageViewModel[]; index: number } => {
  const index = getLastAssistantIndex(messages);
  if (index >= 0 && messages[index].kind === 'assistant_streaming') {
    return { messages, index };
  }

  return {
    messages: [...messages, createAssistantMessage(requestId)],
    index: messages.length,
  };
};

const updateAssistantAt = (
  messages: PhotoSearchMessageViewModel[],
  index: number,
  updater: (
    message: Extract<PhotoSearchMessageViewModel, { role: 'assistant' }>
  ) => PhotoSearchMessageViewModel
): PhotoSearchMessageViewModel[] =>
  messages.map((message, messageIndex) => {
    if (messageIndex !== index || message.role !== 'assistant') {
      return message;
    }
    return updater(message);
  });

const parseEventData = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'string') {
    try {
      return parseEventData(JSON.parse(value));
    } catch (error: unknown) {
      return { message: value };
    }
  }

  return asRecord(value) ?? {};
};

export const normalizePhotoSearchStreamPayload = (
  event: PhotoSearchStreamEventDto | string
): NormalizedStreamPayload => {
  if (typeof event === 'string') {
    try {
      return normalizePhotoSearchStreamPayload(JSON.parse(event) as PhotoSearchStreamEventDto);
    } catch (error: unknown) {
      return {
        type: 'error',
        data: { detail: 'AI 流事件解析失败' },
      };
    }
  }

  const eventRecord = asRecord(event) ?? {};
  const type = asString(eventRecord.type) ?? asString(eventRecord.event) ?? 'message';
  return {
    type,
    data: parseEventData(eventRecord.data ?? {}),
  };
};

const mapFinalEventDto = (data: Record<string, unknown>): PhotoSearchFinalEventDto => {
  const finalData = asRecord(data.result) ?? data;
  return {
    problem_id: asString(finalData.problem_id),
    phase: asString(finalData.phase),
    response_type: asString(finalData.response_type),
    message: asString(finalData.message),
    sections: asSectionDtos(finalData.sections),
    answer_packet: asAnswerPacketDto(finalData.answer_packet),
    can_continue: asBoolean(finalData.can_continue),
    need_more_input: asBoolean(finalData.need_more_input),
    meta: asRecord(finalData.meta),
  };
};

export const applyPhotoSearchStreamEvent = (
  conversation: PhotoSearchConversationViewModel,
  event: PhotoSearchStreamEventDto | string
): PhotoSearchConversationViewModel => {
  const { type, data } = normalizePhotoSearchStreamPayload(event);

  if (type === 'ack') {
    return {
      ...conversation,
      requestId: asString(data.request_id) ?? conversation.requestId,
      sessionId: asString(data.session_id) ?? conversation.sessionId,
      streamStatus: 'streaming',
      loading: true,
      errorMessage: '',
    };
  }

  if (type === 'stage') {
    const stageKey = asString(data.stage) ?? conversation.currentStageKey;
    const stageLabel = asString(data.message) ?? getPhotoSearchStageLabel(stageKey);
    const ensured = ensureStreamingAssistant(conversation.messages, conversation.requestId);
    return {
      ...conversation,
      streamStatus: 'streaming',
      loading: true,
      currentStage: stageLabel,
      currentStageKey: stageKey,
      messages: updateAssistantAt(ensured.messages, ensured.index, (message) => ({
        ...message,
        stage: stageLabel,
      })),
    };
  }

  if (type === 'delta') {
    const delta = asString(data.delta);
    if (!delta) {
      return conversation;
    }

    const stageKey = asString(data.stage) ?? conversation.currentStageKey;
    const stageLabel =
      stageKey === conversation.currentStageKey
        ? conversation.currentStage
        : getPhotoSearchStageLabel(stageKey);
    const ensured = ensureStreamingAssistant(conversation.messages, conversation.requestId);
    return {
      ...conversation,
      streamStatus: 'streaming',
      loading: true,
      currentStage: stageLabel,
      currentStageKey: stageKey,
      messages: updateAssistantAt(ensured.messages, ensured.index, (message) => ({
        ...message,
        content: `${message.content}${delta}`,
        stage: stageLabel,
      })),
    };
  }

  if (type === 'final') {
    const finalEvent = mapFinalEventDto(data);
    const answerPacket = mapPhotoSearchAnswerPacketDtoToViewModel(finalEvent.answer_packet ?? null);
    const needMoreInput = finalEvent.need_more_input === true;
    const safety = mapPhotoSearchSafety(finalEvent.meta ?? null, needMoreInput);
    const knowledgeImpacts = mapPhotoSearchKnowledgeImpacts(finalEvent.meta ?? null);
    const content =
      finalEvent.message ??
      answerPacket?.finalAnswer ??
      answerPacket?.brief ??
      'AI 已生成讲解。';
    const ensured = ensureStreamingAssistant(conversation.messages, conversation.requestId);

    return {
      ...conversation,
      problemId: finalEvent.problem_id ?? conversation.problemId,
      canContinue: finalEvent.can_continue !== false,
      needMoreInput,
      knowledgeImpacts,
      loading: false,
      errorMessage: '',
      messages: updateAssistantAt(ensured.messages, ensured.index, (message) => ({
        ...message,
        kind: 'assistant_final',
        content,
        sections: (finalEvent.sections ?? []).map(mapPhotoSearchSectionDtoToViewModel),
        answerPacket,
        problemId: finalEvent.problem_id ?? null,
        needMoreInput,
        safety,
        knowledgeImpacts,
      })),
    };
  }

  if (type === 'error') {
    const detail =
      asString(data.detail) ??
      asString(data.message) ??
      'AI 解题服务暂时不可用，请稍后重试。';
    const ensured = ensureStreamingAssistant(conversation.messages, conversation.requestId);

    return {
      ...conversation,
      streamStatus: 'error',
      loading: false,
      canContinue: false,
      errorMessage: detail,
      messages: updateAssistantAt(ensured.messages, ensured.index, (message) => ({
        ...message,
        kind: 'assistant_error',
        content: detail,
      })),
    };
  }

  if (type === 'done') {
    return {
      ...conversation,
      streamStatus: conversation.streamStatus === 'error' ? 'error' : 'done',
      currentStage: STAGE_LABELS.done,
      currentStageKey: 'done',
      loading: false,
    };
  }

  return conversation;
};

export const createDemoPhotoSearchFinalEvent = (): PhotoSearchStreamEventDto => ({
  type: 'final',
  data: {
    message: '已进入演示讲解模式。接入 app_backend 后，这里会展示真实流式解题结果。',
    sections: [
      {
        id: 'analysis',
        title: '题意分析',
        items: [{ label: '目标', value: '先识别题干，再拆解已知条件。' }],
      },
      {
        id: 'solution_steps',
        title: '解题步骤',
        items: [
          { step: 1, content: '圈出题目要求，确认要求的是数值、表达式还是证明。' },
          { step: 2, content: '把已知条件转成公式或关键关系。' },
          { step: 3, content: '按知识点逐步推导，并检查答案是否满足题意。' },
        ],
      },
    ],
    answer_packet: {
      brief: '演示讲解已生成。',
      solution: {
        steps: ['识别题干和已知条件', '建立公式或图形关系', '逐步推导并复核结果'],
        final_answer: '接入后端流式结果后显示真实答案。',
      },
    },
    can_continue: true,
    need_more_input: false,
    meta: {
      confidence_level: 'normal',
      knowledge_impacts: [
        {
          id: 'demo-knowledge-1',
          subject: '数学',
          knowledge_point: '题干分析',
          before_status: '未评估',
          after_status: '待巩固',
          change_label: '本次涉及',
        },
      ],
    },
  },
});
